import { useEffect, useRef, useState } from "react";
import { RotateCw } from "lucide-react";
import { isRunningStandalone } from "../../services/pwa";

// The installed app (no browser around it) has no reload: pulling down at the top of a page reloads
// it, like the browser's own pull-to-refresh. Browsers keep their built-in one, so this only runs
// in the installed app on touch screens.

const THRESHOLD = 72; // px of (damped) pull that triggers a reload
const MAX_PULL = 110;
const DAMPING = 0.5;
const INDICATOR = 40;

// Touches that start in these never pull the page: overlays, full-screen views, or anything
// marked data-no-pull (e.g. a map or a canvas).
const IGNORE = '[role="dialog"], .mobile-fullscreen, [data-chat-panel], [data-chat-window], [data-no-pull], input, textarea, select';

const scrolledInside = (el) => {
  for (let node = el; node && node !== document.body; node = node.parentElement) {
    if (node.scrollTop > 0) return true;
  }
  return false;
};

function PullToRefresh() {
  const [enabled] = useState(
    () => isRunningStandalone() && Boolean(window.matchMedia?.("(hover: none) and (pointer: coarse)").matches),
  );
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const pullRef = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;

    const reset = () => {
      startY.current = null;
      pullRef.current = 0;
      setPull(0);
    };

    const onStart = (e) => {
      if (e.touches.length !== 1 || window.scrollY > 0) return;
      if (document.body.style.overflow === "hidden") return; // a full-screen view is open
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest(IGNORE) || scrolledInside(target)) return;
      startY.current = e.touches[0].clientY;
    };

    const onMove = (e) => {
      if (startY.current === null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0 || window.scrollY > 0) {
        if (pullRef.current) reset();
        else startY.current = dy < 0 ? null : startY.current;
        return;
      }
      pullRef.current = Math.min(dy * DAMPING, MAX_PULL);
      setPull(pullRef.current);
    };

    const onEnd = () => {
      if (startY.current === null) return;
      if (pullRef.current >= THRESHOLD) {
        setRefreshing(true);
        setPull(THRESHOLD);
        navigator.vibrate?.(10);
        window.location.reload();
        return;
      }
      reset();
    };

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
    window.addEventListener("touchcancel", reset);
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", reset);
    };
  }, [enabled]);

  if (!enabled || (!pull && !refreshing)) return null;

  const progress = Math.min(pull / THRESHOLD, 1);
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 z-[45] flex justify-center"
      style={{ top: "calc(56px + env(safe-area-inset-top))" }}
    >
      <div
        className="flex items-center justify-center rounded-full border border-app-primary bg-app-card shadow-md"
        style={{
          width: INDICATOR,
          height: INDICATOR,
          transform: `translateY(${pull - INDICATOR / 2}px)`,
          opacity: refreshing ? 1 : progress,
          transition: startY.current === null ? "transform 0.2s ease-out" : undefined,
        }}
      >
        <RotateCw
          size={20}
          className={`${progress >= 1 ? "text-blue-600" : "text-app-muted"} ${refreshing ? "animate-spin" : ""}`}
          style={refreshing ? undefined : { transform: `rotate(${progress * 270}deg)` }}
          aria-label={refreshing ? "Refreshing" : progress >= 1 ? "Release to refresh" : "Pull to refresh"}
        />
      </div>
    </div>
  );
}

export default PullToRefresh;
